import React, { useState, useEffect } from 'react';
import { Eye, Upload, Cpu, CheckCircle2, AlertTriangle, RefreshCw, Layers, ShieldAlert, Trash2, Camera } from 'lucide-react';
import { liveSynth } from '../../core/audio/BinauralEngine';
import { ImageProcessor } from '../../core/ai/imageProcessor';
import { dbStorage } from '../../storage/db';

const VisionLab = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [sensitivity, setSensitivity] = useState(65);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadScanHistory();
  }, []);

  const loadScanHistory = async () => {
    try {
      const records = await dbStorage.getAll('civil_records');
      const scans = records.filter(r => r.type === 'vision_scan');
      setHistory(scans);
    } catch (e) {
      console.warn('Load scans error:', e);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      liveSynth.playClickSfx();
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
        setProcessedImage(null);
        setMetrics(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRunAnalysis = async () => {
    if (!selectedImage) return;
    liveSynth.playClickSfx();
    setIsAnalyzing(true);

    try {
      const result = await ImageProcessor.analyzeImage(selectedImage, Number(sensitivity));
      setProcessedImage(result.processedCanvasUrl);
      setMetrics(result);
      liveSynth.playSuccessSfx();

      // ذخیره نتیجه در دیتابیس
      const scanRecord = {
        id: Date.now().toString(),
        type: 'vision_scan',
        damageRatio: result.damageRatio,
        severity: result.severity,
        detectedClusters: result.detectedClusters,
        date: result.analyzedAt,
        savedAt: new Date().toISOString()
      };
      await dbStorage.put('civil_records', scanRecord);
      setHistory((prev) => [scanRecord, ...prev]);
    } catch (err) {
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    liveSynth.playClickSfx();
    await dbStorage.delete('civil_records', id);
    setHistory((prev) => prev.filter(h => h.id !== id));
  };

  return (
    <div className="space-y-6 pb-24 text-slate-100 max-w-2xl mx-auto text-right" dir="rtl">
      {/* هدر ماژول */}
      <div className="bg-slate-900/90 border border-sky-500/30 rounded-2xl p-5 shadow-xl backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-sky-500/10 border border-sky-500/30 rounded-xl text-sky-400">
            <Eye className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-base font-bold text-sky-400">آزمایشگاه بینایی ماشین و پایش ترک سازه</h1>
            <p className="text-xs text-slate-400">پردازش تصویر سطح بتن و عوارض سازه‌ای (Structural Health Monitoring)</p>
          </div>
        </div>
      </div>

      {/* بخش بارگذاری تصویر و تنظیم حساسیت */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 space-y-4 shadow-lg">
        <div className="border-2 border-dashed border-slate-700 hover:border-sky-400/60 rounded-2xl p-6 text-center transition-colors">
          <input
            type="file" accept="image/*" capture="environment"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            id="vision-upload-input"
          />
          <label htmlFor="vision-upload-input" className="cursor-pointer flex flex-col items-center gap-2">
            <div className="p-3 bg-sky-400/10 rounded-full text-sky-400">
              <Camera className="w-6 h-6" />
            </div>
            <span className="text-xs sm:text-sm font-bold text-slate-200">
              {selectedImage ? 'تصویر بارگذاری شد (برای تغییر کلیک کنید)' : 'انتخاب یا تصویربرداری از سطح بتن / آسفالت'}
            </span>
            <span className="text-[11px] text-slate-500">پشتیبانی از فرمت‌های JPG و PNG</span>
          </label>
        </div>

        {/* تنظیم حساسیت پردازش */}
        <div className="space-y-2 bg-slate-950/60 p-3.5 rounded-xl border border-slate-800">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-300 font-medium">حساسیت آستانه اسکن ترک:</span>
            <span className="text-sky-400 font-mono font-bold">{sensitivity}٪</span>
          </div>
          <input
            type="range"
            min="30"
            max="95"
            value={sensitivity}
            onChange={(e) => setSensitivity(e.target.value)}
            className="w-full accent-sky-400 h-1.5 bg-slate-800 rounded-lg cursor-pointer"
          />
        </div>

        {selectedImage && (
          <button
            type="button"
            onClick={handleRunAnalysis}
            disabled={isAnalyzing}
            className="w-full py-3 bg-sky-400 hover:bg-sky-500 text-slate-950 font-bold text-sm rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-sky-400/10 cursor-pointer disabled:opacity-50"
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>در حال پردازش ماتریس پیکسلی...</span>
              </>
            ) : (
              <>
                <Cpu className="w-4 h-4" />
                <span>اجرای پردازش بینایی ماشین و کادربندی</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* نمایش مقایسه‌ای و نتایج */}
      {processedImage && metrics && (
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 space-y-5 shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold text-sky-400 flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <span>نتیجه تحلیل پیکسلی و کادرهای تشخیصی</span>
            </h3>
            <span className="text-xs px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 font-mono">
              {metrics.detectedClusters} کانون آسیب
            </span>
          </div>

          <div className="rounded-xl overflow-hidden border border-slate-700 bg-black flex items-center justify-center">
            <img src={processedImage} alt="خروجی پردازش شده" className="w-full max-h-96 object-contain" />
          </div>

          {/* کارت‌های متریسی */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 text-center">
              <span className="block text-[11px] text-slate-400 mb-1">نسبت آسیب سطحی</span>
              <span className="text-lg font-black text-sky-400 font-mono">{metrics.damageRatio}٪</span>
            </div>
            <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 text-center">
              <span className="block text-[11px] text-slate-400 mb-1">تعداد کانون‌ها</span>
              <span className="text-lg font-black text-white font-mono">{metrics.detectedClusters}</span>
            </div>
            <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 text-center col-span-2 sm:col-span-1">
              <span className="block text-[11px] text-slate-400 mb-1">سطح ریسک</span>
              <span className={`text-xs font-bold ${metrics.severityColor}`}>{metrics.severity}</span>
            </div>
          </div>
        </div>
      )}

      {/* آرشیو اسکن‌های ذخیره‌شده */}
      {history.length > 0 && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 space-y-3">
          <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-sky-400" />
            <span>آرشیو پایش و ارزیابی‌های سلامت سازه</span>
          </h3>
          <div className="space-y-2">
            {history.map((item) => (
              <div key={item.id} className="flex items-center justify-between bg-slate-950/70 border border-slate-800/80 p-3 rounded-xl text-xs">
                <div>
                  <span className="font-bold text-slate-200 ml-2">آسیب: {item.damageRatio}٪</span>
                  <span className="text-slate-400">({item.severity})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 font-mono">{item.date}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteHistory(item.id)}
                    className="p-1 text-slate-500 hover:text-rose-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisionLab;
